import { Auth0Provider, type AppState, type CacheLocation } from '@auth0/auth0-react';
import { type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

/* https://auth0.com/blog/complete-guide-to-react-user-authentication/ */
/* https://morioh.com/p/995f1244f33b#upgrade-to-react-router-v6 */

type Props = {
  children: ReactNode;
};

const Auth0ProviderWithHistory = ({ children }: Props) => {
  const navigate = useNavigate();

  /* Use the navigate() method to take users back
  to the route they intended to access before authentication. */
  const onRedirectCallback = (appState?: AppState) => {
    navigate(appState?.returnTo || window.location.pathname);
  };

  const domain = import.meta.env['VITE_AUTH0_DOMAIN'] || '';
  const clientId = import.meta.env['VITE_AUTH0_CLIENT_ID'] || '';
  const audience = import.meta.env['VITE_AUTH0_AUDIENCE'] || '';
  // E2E sets this to `localstorage` so Playwright's pre-built storageState
  // (a hand-built `@@auth0spajs@@` cache entry) is consulted by the SDK on
  // boot. Production leaves it unset → SDK default in-memory cache. The
  // `__E2E_AUTH0_CONFIG__` window flag lets auth.setup.ts detect a stale dev
  // server that wasn't started with this env var.
  const cacheLocation = import.meta.env['VITE_AUTH0_CACHE_LOCATION'] as CacheLocation | undefined;
  if (cacheLocation && typeof window !== 'undefined') {
    (window as unknown as { __E2E_AUTH0_CONFIG__?: object }).__E2E_AUTH0_CONFIG__ = {
      clientId, audience, cacheLocation,
    };
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      redirectUri={import.meta.env['VITE_BASE_URL']}
      audience={audience}
      onRedirectCallback={onRedirectCallback}
      scope="openid profile email"
      cacheLocation={cacheLocation}
    >
      {children}
    </Auth0Provider>
  );
};

export default Auth0ProviderWithHistory;
